import React, { useEffect, useState, useCallback } from 'react';
import { api } from '@/lib/api';
import { useI18n, formatCurrency, formatDate } from '@/lib/i18n';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  TrendingUp, TrendingDown, Receipt, Utensils, Package, Sparkles, RefreshCcw,
  Euro, ChefHat, AlertTriangle
} from 'lucide-react';
import {
  LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid,
  BarChart, Bar,
} from 'recharts';

function KpiCard({ icon: Icon, title, value, subtitle, accent = 'primary', testId }) {
  return (
    <Card className="overflow-hidden" data-testid={testId}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="min-w-0">
            <p className="text-xs uppercase tracking-wider text-[hsl(var(--muted-foreground))] font-medium">
              {title}
            </p>
            <p className="text-2xl font-heading font-bold mt-1 tabular-nums truncate">{value}</p>
            {subtitle && <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">{subtitle}</p>}
          </div>
          <div className={`h-10 w-10 rounded-lg grid place-items-center shrink-0 bg-[hsl(var(--${accent === 'primary' ? 'accent' : 'muted'}))]`}>
            <Icon className={`h-5 w-5 text-[hsl(var(--${accent}))]`} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function Dashboard() {
  const { t, lang } = useI18n();
  const [data, setData] = useState(null);
  const [insights, setInsights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [insightsLoading, setInsightsLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/dashboard', { params: { lang } });
      setData(r.data);
    } catch (e) {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.error('Dashboard load failed:', e);
      }
    } finally {
      setLoading(false);
    }
  }, [lang]);

  const loadInsights = useCallback(async () => {
    setInsightsLoading(true);
    try {
      const r = await api.get('/dashboard/insights', { params: { lang } });
      setInsights(r.data.insights || []);
    } catch (e) {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.error('Dashboard insights failed:', e);
      }
      setInsights([]);
    } finally {
      setInsightsLoading(false);
    }
  }, [lang]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-bold tracking-tight">{t('nav_dashboard')}</h1>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">{t('welcome_subtitle')}</p>
        </div>
        <Button variant="outline" size="sm" onClick={load} className="gap-2" data-testid="dashboard-refresh-button">
          <RefreshCcw className="h-4 w-4" />
          {t('refresh')}
        </Button>
      </div>

      {loading || !data ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={`kpi-skeleton-${i}`} className="h-24 w-full rounded-xl" />
          ))}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <KpiCard
              icon={Euro}
              title={t('sales_today')}
              value={formatCurrency(data.sales_today.total, lang)}
              subtitle={`${data.sales_today.count} tickets`}
              testId="kpi-sales-today"
            />
            <KpiCard
              icon={TrendingUp}
              title={t('sales_week')}
              value={formatCurrency(data.sales_week.total, lang)}
              subtitle={`${data.sales_week.count} tickets`}
              testId="kpi-sales-week"
            />
            <KpiCard
              icon={Receipt}
              title={t('expenses_month')}
              value={formatCurrency(data.expenses_month.total, lang)}
              accent="primary"
              testId="kpi-expenses-month"
            />
            <KpiCard
              icon={data.profit_month >= 0 ? TrendingUp : TrendingDown}
              title={t('profit_month')}
              value={formatCurrency(data.profit_month, lang)}
              subtitle={`${t('food_cost')}: ${data.food_cost_pct}%`}
              testId="kpi-profit-month"
            />
            <KpiCard
              icon={Utensils}
              title={t('occupied_tables')}
              value={`${data.tables_occupied}/${data.tables_total}`}
              testId="kpi-tables"
            />
            <KpiCard
              icon={ChefHat}
              title={t('open_orders')}
              value={data.open_orders}
              testId="kpi-open-orders"
            />
            <KpiCard
              icon={Package}
              title={t('low_stock')}
              value={data.low_stock?.length || 0}
              accent="primary"
              testId="kpi-low-stock"
            />
            <KpiCard
              icon={Euro}
              title={t('sales_month')}
              value={formatCurrency(data.sales_month.total, lang)}
              subtitle={`${data.sales_month.count} tickets`}
              testId="kpi-sales-month"
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle className="font-heading">{t('sales_evolution')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-64 w-full">
                  <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={200}>
                    <LineChart data={data.sales_daily}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis
                        dataKey="fecha"
                        tickFormatter={(v) => formatDate(v, lang).slice(0, 5)}
                        fontSize={11}
                        stroke="hsl(var(--muted-foreground))"
                      />
                      <YAxis fontSize={11} stroke="hsl(var(--muted-foreground))" />
                      <Tooltip
                        formatter={(v) => formatCurrency(v, lang)}
                        labelFormatter={(v) => formatDate(v, lang)}
                        contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 8 }}
                      />
                      <Line
                        type="monotone"
                        dataKey="total"
                        stroke="hsl(var(--primary))"
                        strokeWidth={2.5}
                        dot={{ fill: 'hsl(var(--primary))', r: 3 }}
                        activeDot={{ r: 5 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex-row items-center justify-between space-y-0">
                <CardTitle className="font-heading flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-[hsl(var(--primary))]" />
                  {t('ai_insights')}
                </CardTitle>
                <Button size="sm" variant="ghost" onClick={loadInsights} disabled={insightsLoading} data-testid="insights-refresh-button">
                  <RefreshCcw className={`h-3 w-3 ${insightsLoading ? 'animate-spin' : ''}`} />
                </Button>
              </CardHeader>
              <CardContent>
                {insights.length === 0 ? (
                  <div className="text-center py-6">
                    <p className="text-sm text-[hsl(var(--muted-foreground))] mb-3">
                      {lang === 'es' ? 'Genera insights con IA basados en tus datos.' : 'Gere insights com IA baseados nos seus dados.'}
                    </p>
                    <Button size="sm" onClick={loadInsights} disabled={insightsLoading} data-testid="insights-generate-button">
                      <Sparkles className="h-3 w-3 mr-2" />
                      {insightsLoading ? t('ai_loading') : t('ai_insights')}
                    </Button>
                  </div>
                ) : (
                  <ul className="space-y-2">
                    {insights.map((ins, i) => (
                      <li key={`insight-${i}-${(ins || '').slice(0, 24)}`} className="flex gap-2 text-sm">
                        <span className="h-1.5 w-1.5 rounded-full bg-[hsl(var(--primary))] mt-2 shrink-0" />
                        <span>{ins}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle className="font-heading">{t('top_dishes')}</CardTitle>
              </CardHeader>
              <CardContent>
                {data.top_dishes && data.top_dishes.length > 0 ? (
                  <div className="h-56 w-full">
                    <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={180}>
                      <BarChart data={data.top_dishes} layout="vertical" margin={{ left: 20 }}>
                        <XAxis type="number" stroke="hsl(var(--muted-foreground))" fontSize={11} />
                        <YAxis dataKey="_id" type="category" width={120} stroke="hsl(var(--muted-foreground))" fontSize={11} />
                        <Tooltip
                          formatter={(v) => [v, t('quantity')]}
                          contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 8 }}
                        />
                        <Bar dataKey="cantidad" fill="hsl(var(--secondary))" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <p className="text-center py-8 text-sm text-[hsl(var(--muted-foreground))]">{t('no_data')}</p>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="font-heading flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-[hsl(43_92%_45%)]" />
                  {t('low_stock')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {data.low_stock && data.low_stock.length > 0 ? (
                  <div className="space-y-2">
                    {data.low_stock.slice(0, 8).map((p) => (
                      <div key={p.id} className="flex items-center justify-between p-2 rounded-lg bg-[hsl(43_92%_96%)] text-[hsl(30_80%_22%)]">
                        <div className="min-w-0">
                          <p className="text-sm font-medium truncate">{p.nombre}</p>
                          <p className="text-xs opacity-70">{p.categoria || '-'}</p>
                        </div>
                        <Badge variant="outline" className="font-mono text-xs">
                          {p.stock_actual} / {p.stock_minimo} {p.unidad}
                        </Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center py-8 text-sm text-[hsl(var(--muted-foreground))]">{t('no_data')}</p>
                )}
              </CardContent>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}
